import type { BillingPrice, SignupSource } from '@floorpoll/shared';
import { analyticsEnabled } from './gtag.js';
import { pagePath } from './pagePath.js';

/**
 * The events this app sends Google Analytics, and the only way to send one.
 *
 * **What earns a place here is narrow, and the rule comes from GROWTH.md:**
 * GA is not authoritative for signups, activation or the `join` share — those
 * are first-party counts this system owns, and a second set of numbers for
 * them would only disagree with the operator dashboard. What GA adds is "the
 * half the counters cannot see". So the test an event has to pass is: *does
 * this describe something the API never hears about?*
 *
 * That admits the moments just *before* a request, and the ones where a
 * request failed or was never made. It excludes `vote_cast`, `run_started`,
 * `poll_created` and anything resembling a purchase — the table and Stripe
 * already own those, exactly, and GA would only estimate them.
 *
 * **Nothing is sent from the audience's phone.** A taker's device counts page
 * views and nothing else. The Privacy Policy tells a voter the page view was
 * counted while the poll ran, and the surface where "nobody can see how an
 * individual voted" has to stay true is not the place to start a behavioural
 * event stream. That is enforced on the send — see `AUDIENCE_SURFACES` — so it
 * holds for an event added later and for a component shared with a phone.
 *
 * **Every parameter is a closed set, and that is enforced by the type rather
 * than by review.** Event parameters are a second channel to Google that does
 * not pass through `pagePath`, and this app's URLs carry credentials: a
 * `quizId` is effectively the results key, a join code and an invite token are
 * bearer capabilities. A `Record<string, unknown>` payload would put all of
 * them one careless call away, so there is no such function — a caller can
 * only construct one of the shapes below, whose fields are literal unions and
 * enums from `shared`. An id cannot be expressed, which is the same reason
 * `publicOption` builds from an allowlist: the next field is refused by
 * default rather than caught in review. Closed sets are also what GA4 wants of
 * a custom dimension, which would buckle under unbounded cardinality.
 *
 * Names are deliberately custom rather than GA4's recommended `sign_up` or
 * `select_content`: those carry expected shapes, and a recommended name with
 * the wrong shape is mis-bucketed in reports rather than rejected.
 */

/** Where a public call to action was clicked, for pages carrying several. */
export type CtaSlot = 'hero' | 'body' | 'footer' | 'nav';

/**
 * Why a signup attempt produced no account.
 *
 * Two values, and deliberately not the server's message. `ApiError.message` is
 * written for the person in front of it and may quote what they typed, so
 * forwarding it would send an email address to Google under the name of a
 * failure reason. The distinction worth having is narrower anyway: the API
 * said no, or it was never reached.
 */
export type SignupFailure = 'rejected' | 'unreachable';

export type AnalyticsEvent =
  /**
   * The signup form has been engaged — a first keystroke, not a submission.
   *
   * The most valuable of these four. The API hears about accounts that were
   * created; nothing hears about the person who filled in two fields and left.
   * That gap is the form's real conversion rate and it is invisible today.
   */
  | { name: 'signup_start' }
  /** An attempt that ended without an account. Pairs with the above. */
  | { name: 'signup_failed'; reason: SignupFailure }
  /**
   * A public call to action was clicked.
   *
   * Marketing pages currently report arrival and nothing else, so a page that
   * is read and a page that is acted on look identical. `via` is the channel
   * the page stands in — the same bounded slug the signup link already
   * carries, so a comparison page added next month is counted without anyone
   * touching its button.
   */
  | { name: 'cta_click'; via: SignupSource; slot: CtaSlot }
  /**
   * Checkout was asked for, before the Stripe session exists.
   *
   * Stripe records sessions it created; the click that failed to become one —
   * because billing was unreachable, or because the person thought better of
   * it at the card form — is between the two systems and in neither.
   */
  | { name: 'upgrade_clicked'; price: BillingPrice };

/**
 * Every event name, as a value rather than a type.
 *
 * It exists so the Privacy Policy can be held to it. That page enumerates what
 * Google receives and says the list is complete, and `legal.test.tsx` pins
 * this length against the prose there — so a fifth event fails the legal test
 * until the page describing it is rewritten. The type assertion below is the
 * other half: a variant added to the union without being added here is a build
 * error, so the list cannot fall behind the code either.
 */
export const ANALYTICS_EVENTS = [
  'signup_start',
  'signup_failed',
  'cta_click',
  'upgrade_clicked',
] as const;

/** Compile-time proof that the list above and the union agree, both ways. */
type Exhaustive<A, B extends A> = [A, B];
type _NamesMatch = Exhaustive<AnalyticsEvent['name'], (typeof ANALYTICS_EVENTS)[number]>;
type _ListMatch = Exhaustive<(typeof ANALYTICS_EVENTS)[number], AnalyticsEvent['name']>;

/**
 * The surfaces an audience meets, where no event is ever sent.
 *
 * The rule above is "nothing from the audience's phone", and this is where it
 * is kept rather than at each call site. It has to be structural because the
 * surfaces are not separate components: `SignupLink` is one shared button, and
 * the ended screen of a poll offers it to a voter on the same phone that has
 * just been voting. A rule living in each caller would hold exactly until
 * someone reused a component, which has already happened once.
 *
 * Kept as a guard on the send rather than a condition on one event, so an
 * event added later is withheld here by default instead of inheriting the
 * question. Page views are unaffected — those are disclosed, and counting the
 * join page is what the Privacy Policy already describes to a voter.
 *
 * `/j` and `/j/:code` are the join screens, `/screen/:id` the big screen.
 * These are the "Audience surfaces" block of `APP_ROUTE_PATTERNS`; there is no
 * exported list to derive from, so a fourth audience route added there needs
 * adding here too.
 */
const AUDIENCE_SURFACES = /^\/(j|screen)(\/|$)/;

/**
 * Sends one event, with the current route pattern attached as `from`.
 *
 * `from` is computed here rather than passed in, which is the point: it goes
 * through `pagePath`, the one audited redaction in this app, so an event
 * cannot describe its own location in a form a page view would not have been
 * allowed to. A caller has no way to name a page at all.
 *
 * Guarded exactly as `trackPageView` is. Without a measurement id there is no
 * `window.gtag`, and a build that ships no analytics must behave as it did
 * before analytics existed — in a local dev run and in every test.
 */
export function trackEvent(event: AnalyticsEvent): void {
  if (!analyticsEnabled()) return;
  if (AUDIENCE_SURFACES.test(window.location.pathname)) return;

  const { name, ...params } = event;
  window.gtag?.('event', name, {
    ...params,
    from: pagePath(window.location.pathname, window.location.search),
  });
}
